/**
 * @description Hook for resolving tenant branding from domain
 *
 * Key features:
 * - Tier-2: parses *.lynx.build slug → public branding API (SO or CP occupancy)
 * - Tier-3: same-origin GET /api/public/tenant/resolve → branding from payload
 * - Platform hosts: platform_config → LynxPay defaults
 * - CP/SO slug chrome overlays Lynx/generic — never LynxPay first, then swap
 *
 * @dependencies
 * - brandingService: Public branding / platform-config fetch
 * - tenantResolveService: Tier-3 host → SO resolve
 * - brandingDefaults: LynxPay vs Lynx/generic overlays
 *
 * @notes
 * - Documented walk: site → company → SO → CP → platform_config only if
 *   tenantRoot is platform. This hook implements slug read + overlay merge.
 *   Company / site hops and tenantRoot stay LYN-160 / websites.
 * - Domain detection: *.lynx.build slug → fetch occupancy branding;
 *   verified custom host → resolve (160 owns hosts); platform → LynxPay.
 * - Cache key: `branding_{slug}` or `platform_config`, TTL: 5 minutes
 */

import { useState, useEffect } from 'react';
import { fetchTenantBranding, fetchPlatformConfig } from '../services/api/brandingService';
import { resolveTenantForHostname } from '../services/api/tenantResolveService';
import LYNXPAY_DEFAULTS, {
  LYNX_GENERIC_OVERLAY,
  mergeTenantBranding,
  overlayForHostKind,
} from '../constants/brandingDefaults';
import { getLynxBuildTenantSlug, isPlatformHostname } from '../utils/lynxBuildHostname';

const CACHE_PREFIX = 'branding_';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * LYN-309: do not infer platformName / documentTitle from organizationName
 * (channel_partners.name or sales_organizations.organization_name). Empty
 * identity fills Lynx/generic at merge time.
 *
 * @param {object} merged
 * @returns {object}
 */
function applyOrgNameFallbacks(merged) {
  return merged;
}

/**
 * Read cached branding from sessionStorage
 */
function getCachedBranding(slug) {
  try {
    const cached = sessionStorage.getItem(CACHE_PREFIX + slug);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_PREFIX + slug);
      return null;
    }

    return data;
  } catch {
    return null;
  }
}

/**
 * Write branding to sessionStorage cache
 */
function setCachedBranding(slug, data) {
  try {
    sessionStorage.setItem(CACHE_PREFIX + slug, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch {
    // SessionStorage full or unavailable — ignore
  }
}

function initialOverlayForWindow() {
  if (typeof window === 'undefined') {
    return LYNX_GENERIC_OVERLAY;
  }
  const hostname = window.location.hostname;
  if (isPlatformHostname(hostname)) {
    return overlayForHostKind('platform');
  }
  return overlayForHostKind('slug');
}

/**
 * Hook: Resolve tenant branding from current domain
 * @returns {{
 *   branding: object,
 *   isLoading: boolean,
 *   isDefault: boolean,
 *   slug: string|null,
 *   signupEnabled: boolean,
 *   occupancy: object|null,
 *   organizationName: string|null
 * }}
 */
export default function useTenantBranding() {
  const [branding, setBranding] = useState(initialOverlayForWindow);
  const [isLoading, setIsLoading] = useState(true);
  const [isDefault, setIsDefault] = useState(() => (
    typeof window !== 'undefined' && isPlatformHostname(window.location.hostname)
  ));
  const [slug, setSlug] = useState(null);
  const [signupEnabled, setSignupEnabled] = useState(true);
  const [occupancy, setOccupancy] = useState(null);
  const [organizationName, setOrganizationName] = useState(null);

  useEffect(() => {
    const hostname = window.location.hostname;
    const lynxSlug = getLynxBuildTenantSlug(hostname);
    let cancelled = false;

    async function load() {
      // Tier-2 sync slug path (SO or CP occupancy — same public URL)
      if (lynxSlug) {
        setSlug(lynxSlug);
        const overlay = overlayForHostKind('slug');
        const cached = getCachedBranding(lynxSlug);
        if (cached) {
          const merged = applyOrgNameFallbacks(
            mergeTenantBranding(overlay, cached.branding)
          );
          if (!cancelled) {
            setBranding(merged);
            setSignupEnabled(cached.signupEnabled !== false);
            setOccupancy(cached.occupancy || null);
            setOrganizationName(cached.organizationName || null);
            setIsDefault(false);
            setIsLoading(false);
          }
          return;
        }

        const result = await fetchTenantBranding(lynxSlug);
        if (cancelled) return;
        if (result) {
          setCachedBranding(lynxSlug, result);
          setBranding(applyOrgNameFallbacks(
            mergeTenantBranding(overlay, result.branding)
          ));
          setSignupEnabled(result.signupEnabled !== false);
          setOccupancy(result.occupancy || null);
          setOrganizationName(result.organizationName || null);
          setIsDefault(false);
        } else {
          setBranding(overlay);
          setSignupEnabled(true);
          setOccupancy(null);
          setOrganizationName(null);
          setIsDefault(false);
        }
        setIsLoading(false);
        return;
      }

      // Platform hosts → platform_config / LynxPay defaults
      if (isPlatformHostname(hostname)) {
        setSlug(null);
        const overlay = overlayForHostKind('platform');
        const cachedPlatform = getCachedBranding('platform_config');
        if (cachedPlatform) {
          if (!cancelled) {
            setBranding(mergeTenantBranding(overlay, cachedPlatform.branding));
            setSignupEnabled(true);
            setOccupancy(null);
            setOrganizationName(null);
            setIsDefault(true);
            setIsLoading(false);
          }
          return;
        }

        const result = await fetchPlatformConfig();
        if (cancelled) return;
        if (result) {
          setCachedBranding('platform_config', result);
          setBranding(mergeTenantBranding(overlay, result.branding));
        } else {
          setBranding(LYNXPAY_DEFAULTS);
        }
        setSignupEnabled(true);
        setOccupancy(null);
        setOrganizationName(null);
        setIsDefault(true);
        setIsLoading(false);
        return;
      }

      // Tier-3 custom host → resolve (fail closed on 404). Overlay is Lynx/generic.
      const overlay = overlayForHostKind('custom');
      const resolved = await resolveTenantForHostname(hostname);
      if (cancelled) return;

      if (!resolved?.slug) {
        setSlug(null);
        setBranding(overlay);
        setSignupEnabled(true);
        setOccupancy(null);
        setOrganizationName(null);
        setIsDefault(false);
        setIsLoading(false);
        return;
      }

      setSlug(resolved.slug);
      const cacheKey = resolved.slug;
      const occupancyPayload = {
        kind: 'so',
        slug: resolved.slug,
        connectedVanityDomain: null
      };
      const payload = {
        organizationName: resolved.organizationName,
        branding: resolved.branding || {},
        signupEnabled: resolved.signupEnabled !== false,
        occupancy: occupancyPayload
      };
      setCachedBranding(cacheKey, payload);
      setBranding(applyOrgNameFallbacks(
        mergeTenantBranding(overlay, payload.branding)
      ));
      setSignupEnabled(payload.signupEnabled);
      setOccupancy(occupancyPayload);
      setOrganizationName(resolved.organizationName || null);
      setIsDefault(false);
      setIsLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, []);

  return { branding, isLoading, isDefault, slug, signupEnabled, occupancy, organizationName };
}
